import { AccountsContactToggles } from "@/components/non_registrant/accounts-contact-toggles";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM, UserType } from "@/constants";
import {
  getNonRegistrantRegistrantAccountLoginIdentitiesOptions,
  getNonRegistrantRegistrantAccountOptions,
  getNonRegistrantRegistrantAccountQueryKey,
  operatorDisableRegistrantUserMutation,
  operatorReenableRegistrantUserMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  type Options,
  operatorAcceptPendingRegistrantInvite,
} from "@/hera-api/client/sdk.gen";
import type { OperatorAcceptPendingRegistrantInviteData } from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { isHeyApiQuery } from "@/lib/hey-api-query-key";
import { organizationRoleLabel } from "@/lib/registrant-display";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

interface RegistrantDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  registrantAccountId: string | null;
  /** When provided and registrant has a company name, clicking it opens organization details with this id (use account.id for primary accounts). */
  onOpenOrganization?: (organizationId: string) => void;
  /** Show "Impersonate" button that opens the registrant portal in a new tab as this registrant. Operator portal only. */
  showImpersonateButton?: boolean;
  /** When set, staff can edit accounts-contact flags for portal logins in this org. */
  staffPortal?: UserType.OPERATOR | UserType.GOVERNMENT;
}

function formatValue(value: string | null | undefined): string {
  return value ?? "—";
}

export function RegistrantDetailsSheet({
  isOpen,
  onOpenChange,
  registrantAccountId,
  onOpenOrganization,
  showImpersonateButton = false,
  staffPortal,
}: RegistrantDetailsSheetProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const queryClient = useQueryClient();
  const [showAccountActionDialog, setShowAccountActionDialog] = useState(false);

  const refreshRegistrant = async () => {
    if (!registrantAccountId) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: getNonRegistrantRegistrantAccountQueryKey({
        path: { registrant_account_id: registrantAccountId },
        query: { include_deleted: true },
      }),
    });
    await queryClient.invalidateQueries({
      predicate: (q) =>
        isHeyApiQuery(
          q.queryKey,
          "getNonRegistrantRegistrantAccounts",
          "getNonRegistrantOrganization",
          "getNonRegistrantOrganizations",
        ),
      refetchType: "all",
    });
  };

  const disableUserMutation = useMutation({
    ...operatorDisableRegistrantUserMutation(),
    onSuccess: async () => {
      await refreshRegistrant();
      setShowAccountActionDialog(false);
      toast({
        title: "User disabled",
        description:
          account?.payment_account_id === account?.id && account?.primary_id
            ? "The user can no longer log in or receive license transfers. Billing was moved to the primary account."
            : "The user can no longer log in or receive license transfers.",
        variant: "secondary",
      });
    },
    onError: (error) => {
      toast({
        title: "Could not disable user",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const reenableUserMutation = useMutation({
    ...operatorReenableRegistrantUserMutation(),
    onSuccess: async () => {
      await refreshRegistrant();
      setShowAccountActionDialog(false);
      toast({
        title: "User re-enabled",
        description: "The registrant user can access the portal again.",
        variant: "secondary",
      });
    },
    onError: (error) => {
      toast({
        title: "Could not re-enable user",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const acceptInviteMutation = useMutation({
    mutationFn: async (
      variables: Options<OperatorAcceptPendingRegistrantInviteData>,
    ) => {
      await operatorAcceptPendingRegistrantInvite({
        ...variables,
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      if (!registrantAccountId) {
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: getNonRegistrantRegistrantAccountQueryKey({
          path: { registrant_account_id: registrantAccountId },
          query: { include_deleted: true },
        }),
      });
      await queryClient.invalidateQueries({
        predicate: (q) =>
          isHeyApiQuery(
            q.queryKey,
            "getNonRegistrantOrganization",
            "getNonRegistrantOrganizations",
          ),
        refetchType: "all",
      });
      toast({
        title: "Invitation accepted",
        description: "This account is now linked to the organization.",
        variant: "secondary",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not accept invitation",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const accountsPortal =
    staffPortal === UserType.GOVERNMENT ? "government" : "operator";

  const {
    data: loginIdentitiesData,
    isLoading: isLoginIdentitiesLoading,
    error: loginIdentitiesError,
  } = useQuery({
    ...getNonRegistrantRegistrantAccountLoginIdentitiesOptions({
      path: { registrant_account_id: registrantAccountId ?? "" },
    }),
    enabled:
      Boolean(staffPortal) &&
      isAuthSetupComplete &&
      isOpen &&
      Boolean(registrantAccountId),
  });

  const {
    data: account,
    isLoading,
    error,
  } = useQuery({
    ...getNonRegistrantRegistrantAccountOptions({
      path: { registrant_account_id: registrantAccountId ?? "" },
      query: { include_deleted: true },
    }),
    enabled: isOpen && !!registrantAccountId,
  });
  const primaryDisableBlocked = Boolean(
    account &&
      !account.primary_id &&
      !account.is_deleted &&
      (account.secondary_count ?? 0) > 0,
  );
  const disablingSelectedBillingSecondary = Boolean(
    account &&
      !account.is_deleted &&
      account.primary_id &&
      account.payment_account_id === account.id,
  );
  const disablingPaymentEnabledUser = Boolean(
    account && !account.is_deleted && account.can_make_payments,
  );

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Registrant Details</SheetTitle>
            <SheetDescription className="hidden">
              View registrant account information
            </SheetDescription>
          </SheetHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4">
            {!registrantAccountId ? (
              <p className="text-muted-foreground text-sm">
                Select a registrant to view details.
              </p>
            ) : isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingScreen message="Loading registrant details..." />
              </div>
            ) : error ? (
              <ErrorComponent
                title={getQueryErrorHeading(error)}
                message={getQueryErrorMessage(error)}
              />
            ) : account ? (
              (() => {
                // For secondary accounts, show primary's company name and address in the main fields
                const displayCompanyName =
                  account.primary_id && account.primary_account_company_name
                    ? account.primary_account_company_name
                    : account.company_name;
                const displayAddress =
                  account.primary_id &&
                  (account.primary_account_address1 ||
                    account.primary_account_address2 ||
                    account.primary_account_city)
                    ? [
                        account.primary_account_address1,
                        account.primary_account_address2,
                        [
                          account.primary_account_city,
                          account.primary_account_state,
                          account.primary_account_zipcode,
                        ]
                          .filter(Boolean)
                          .join(", "),
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : [
                        account.address1,
                        account.address2,
                        [account.city, account.state, account.zipcode]
                          .filter(Boolean)
                          .join(", "),
                      ]
                        .filter(Boolean)
                        .join(", ");
                const organizationIdForLink = account.primary_id ?? account.id;
                const showOperatorAcceptInvite =
                  staffPortal === UserType.OPERATOR &&
                  Boolean(account.primary_id_pending) &&
                  !account.primary_id;
                return (
                  <div className="w-full space-y-6">
                    <Card className="gap-4">
                      <CardHeader className="flex flex-row items-start justify-between">
                        <CardTitle className="font-semibold text-xl">
                          Registrant
                        </CardTitle>
                        <div className="flex gap-2">
                          {staffPortal === UserType.OPERATOR ? (
                            <Button
                              size="sm"
                              variant={
                                account.is_deleted ? "outline" : "destructive"
                              }
                              onClick={() => setShowAccountActionDialog(true)}
                            >
                              {account.is_deleted ? "Re-enable" : "Disable"}
                            </Button>
                          ) : null}
                          {showImpersonateButton ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                window.open(
                                  `/registrant/dashboard?${REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM}=${account.id}`,
                                  "_blank",
                                )
                              }
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Impersonate
                            </Button>
                          ) : null}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 grid-cols-2 items-start">
                          <div>
                            <p className="text-sm font-bold">Email</p>
                            <p className="text-sm">
                              {formatValue(account.email)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">Name</p>
                            <p className="text-sm">
                              {formatValue(account.name)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">Company name</p>
                            {displayCompanyName && onOpenOrganization ? (
                              <button
                                type="button"
                                onClick={() =>
                                  onOpenOrganization(organizationIdForLink)
                                }
                                className="text-sm text-primary underline underline-offset-2 hover:no-underline text-left"
                              >
                                {displayCompanyName}
                              </button>
                            ) : (
                              <p className="text-sm">
                                {formatValue(displayCompanyName)}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-bold">
                              Organization role
                            </p>
                            <p className="text-sm">
                              {organizationRoleLabel(account)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">Phone</p>
                            <p className="text-sm">
                              {formatValue(account.phone)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-bold">Status</p>
                            <p className="text-sm">
                              {account.is_deleted ? "Deleted" : "Active"}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-sm font-bold">Address</p>
                            <p className="text-sm">{displayAddress || "—"}</p>
                          </div>
                          {account.primary_id ? (
                            <div className="col-span-2">
                              <p className="text-sm font-bold">
                                Primary account
                              </p>
                              <p className="text-sm">
                                {formatValue(account.primary_account_name)}
                                {account.primary_account_company_name
                                  ? ` (${account.primary_account_company_name})`
                                  : ""}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                    {showOperatorAcceptInvite ? (
                      <Card className="gap-4">
                        <CardHeader>
                          <CardTitle className="text-base">
                            Pending organization invitation
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm text-muted-foreground">
                            This account has been invited to join an
                            organization but has not accepted yet.
                          </p>
                          <Button
                            type="button"
                            disabled={acceptInviteMutation.isPending}
                            onClick={() =>
                              void acceptInviteMutation.mutateAsync({
                                path: {
                                  registrant_account_id:
                                    registrantAccountId ?? "",
                                },
                              })
                            }
                          >
                            {acceptInviteMutation.isPending
                              ? "Accepting…"
                              : "Accept invitation"}
                          </Button>
                        </CardContent>
                      </Card>
                    ) : null}
                    {staffPortal ? (
                      <Card className="gap-4">
                        <CardHeader>
                          <CardTitle className="text-base">
                            Accounts contact (invoices)
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {isLoginIdentitiesLoading ? (
                            <p className="text-sm text-muted-foreground">
                              Loading portal users…
                            </p>
                          ) : loginIdentitiesError ? (
                            <ErrorComponent
                              title={getQueryErrorHeading(loginIdentitiesError)}
                              message={getQueryErrorMessage(
                                loginIdentitiesError,
                              )}
                            />
                          ) : loginIdentitiesData ? (
                            <AccountsContactToggles
                              portal={accountsPortal}
                              loginIdentities={
                                loginIdentitiesData.login_identities ?? []
                              }
                              preLoginAccountsContact={
                                loginIdentitiesData.pre_login_accounts_contact ??
                                []
                              }
                            />
                          ) : null}
                        </CardContent>
                      </Card>
                    ) : null}
                  </div>
                );
              })()
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog
        open={showAccountActionDialog}
        onOpenChange={setShowAccountActionDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {primaryDisableBlocked
                ? "Cannot disable primary account"
                : account?.is_deleted
                  ? "Re-enable registrant user"
                  : "Disable registrant user"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {primaryDisableBlocked
                ? `This is a primary account and there are active secondary accounts for ${account?.company_name || account?.name || account?.email}. Please disable the secondary accounts first.`
                : account?.is_deleted
                  ? `Restore portal access for ${account.name || account.email}?`
                  : `Disable ${account?.name || account?.email}? They will no longer be able to log in or receive license transfers. Existing licenses and history will be preserved.${disablingSelectedBillingSecondary ? " This is the billing account, so billing will move to the primary account." : ""}${disablingPaymentEnabledUser ? " If this is the only active user allowed to make payments, the primary user will be given payment permission." : ""}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {primaryDisableBlocked ? (
              <AlertDialogCancel>Close</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel
                  disabled={
                    disableUserMutation.isPending ||
                    reenableUserMutation.isPending
                  }
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  type="button"
                  className={
                    account?.is_deleted
                      ? undefined
                      : "bg-destructive hover:bg-destructive/80 text-destructive-foreground"
                  }
                  disabled={
                    disableUserMutation.isPending ||
                    reenableUserMutation.isPending
                  }
                  onClick={(event) => {
                    event.preventDefault();
                    if (!account) {
                      return;
                    }
                    const variables = {
                      path: { registrant_account_id: account.id },
                    };
                    if (account.is_deleted) {
                      reenableUserMutation.mutate(variables);
                    } else {
                      disableUserMutation.mutate(variables);
                    }
                  }}
                >
                  {disableUserMutation.isPending ||
                  reenableUserMutation.isPending
                    ? "Saving…"
                    : account?.is_deleted
                      ? "Re-enable"
                      : "Disable"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
